import { Box, IconButton, Tooltip } from '@mui/material'
import { Done, Error as ErrorIcon, FileDownload, LocalCafe, Sync } from '@mui/icons-material'
import { isNil } from 'lodash'
import moment from 'moment'
import React from 'react'
import i18n from 'simple-react-i18n'
import ProgressBar from '../components/progress/ProgressBar'
import { contentsPath } from '../conf/basepath'
import { EXPORT_JOB_STATUS } from '../export/constants/ExportConstants'
import { shortenHumanize } from './DateUtil'

const ONE_DAY = 24 * 60 * 60 * 1000
const MAX_DISPLAYED_DURATION = 2 * ONE_DAY
const DURATION_STATUS = [EXPORT_JOB_STATUS.IN_PROGRESS, EXPORT_JOB_STATUS.FINISHED, EXPORT_JOB_STATUS.ERROR]

const getStatusLabel = (status) => {
    switch (status) {
        case EXPORT_JOB_STATUS.WAITING:
            return i18n.waiting
        case EXPORT_JOB_STATUS.IN_PROGRESS:
            return i18n.inProgress
        case EXPORT_JOB_STATUS.FINISHED:
            return i18n.success
        default:
            return i18n.error
    }
}

const getStatusColor = (status) => {
    switch (status) {
        case EXPORT_JOB_STATUS.WAITING:
            return 'grey'
        case EXPORT_JOB_STATUS.IN_PROGRESS:
            return 'blue'
        case EXPORT_JOB_STATUS.FINISHED:
            return 'green'
        default:
            return 'red'
    }
}

const getStatusIcon = (status) => {
    const StatusIcon = (() => {
        switch (status) {
            case EXPORT_JOB_STATUS.WAITING:
                return LocalCafe
            case EXPORT_JOB_STATUS.IN_PROGRESS:
                return Sync
            case EXPORT_JOB_STATUS.FINISHED:
                return Done
            default:
                return ErrorIcon
        }
    })()
    return <StatusIcon sx={{ color: getStatusColor(status), marginLeft: '10px' }} />
}

const getStatusCell = (status) => ({
    value: <Tooltip title={getStatusLabel(status)}>{getStatusIcon(status)}</Tooltip>,
    noPadding: true,
    sortValue: status,
})

// Un export programmé conserve sa date de création d'origine : passé 2 jours, l'écart
// création -> exécution ne mesure plus une durée d'exécution, on n'affiche donc rien
const getExportDuration = ({ status, creationDate, updateDate }) => {
    if (!DURATION_STATUS.includes(status)) {
        return ''
    }
    const end = status === EXPORT_JOB_STATUS.IN_PROGRESS ? moment().valueOf() : updateDate
    if (isNil(end) || isNil(creationDate)) {
        return ''
    }
    const duration = moment(end).diff(moment(creationDate))
    if (duration < 0 || duration > MAX_DISPLAYED_DURATION) {
        return ''
    }
    return duration >= ONE_DAY ? shortenHumanize(duration, { units: ['d', 'h'], round: true }) : shortenHumanize(duration)
}

const getLoadingBar = (status, progress, filePath) => {
    if (status === EXPORT_JOB_STATUS.FINISHED) {
        return (
            <Tooltip title={i18n.download}>
                <IconButton size='small' sx={{ color: 'black' }} href={`${contentsPath}${filePath}`}>
                    <FileDownload />
                </IconButton>
            </Tooltip>
        )
    } else if (status === EXPORT_JOB_STATUS.ERROR) {
        return ''
    }
    return <ProgressBar progress={progress} withMessage/>
}

const STATUS_HEADER = { key: 'status', width: '60px' }
const FILE_LABEL_HEADER = { key: 'fileLabel', width: '250px' }
const SCHEDULED_FILE_LABEL_HEADER = { key: 'fileLabel', width: '150px' }
const SCHEDULED_NAME_HEADER = { key: 'name', width: '110px' }
const SCHEDULED_JOB_HEADER = { key: 'job', width: '150px' }

const getEllipsisCell = (label = '') => ({
    value: (
        <Tooltip title={label}>
            <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</Box>
        </Tooltip>
    ),
    sortValue: label,
})

export { getStatusIcon, getStatusColor, getStatusLabel, getStatusCell, getExportDuration, getLoadingBar, STATUS_HEADER, FILE_LABEL_HEADER, SCHEDULED_FILE_LABEL_HEADER, SCHEDULED_NAME_HEADER, SCHEDULED_JOB_HEADER, getEllipsisCell }
